import axios from 'axios';

import configurations from '@/config';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { getAccessToken } from '@/utils/api';

/**
 * This converts a snake_case prompt name into a more friendly version.
 *
 * @param promptName - Name of a preset Template that is shown to the user, used
 * to decide which instruction prompt to run the transcribed audio against.
 * @returns The prompt name string, with multiple words separated by a space, and
 * all words capitalized.
 */
export const displayHumanReadableTemplateName = (promptName: string) => {
  return promptName.split('_').join(' ').toUpperCase();
};

//--- Helper functions related to actually saving and generating the clinical note

/**
 * The size of each file chunk to upload, set to 5MB (the minimum part size for AWS S3 multipart uploads).
 */
export const CHUNK_SIZE = 5 * 1024 * 1024;

/**
 * Converts a media blob URL into a `File` object with a `.webm` extension.
 *
 * @param {string} mediaBlobUrl - The URL of the media blob to convert.
 * @param {string | undefined} id - An optional identifier (e.g. appointment ID) used to name the file.
 * @returns {Promise<File>} A promise that resolves to a `File` object.
 */
export const convertMediaBlobToFile = async (
  mediaBlobUrl: string,
  id: string | undefined,
): Promise<File> => {
  const blob = await fetch(mediaBlobUrl).then((r) => r.blob());
  const fileName = id === undefined ? 'no-appointment-id' : id;
  return new File([blob], `${fileName}.webm`, { type: 'audio/webm' });
};

/**
 * Initiates a multipart upload by requesting an upload ID and key from the server.
 *
 * @param {string} filename - The name of the file to upload.
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @returns {Promise<{ uploadId: string; key: string }>} A promise that resolves to the upload metadata.
 */
export const initiateUpload = async (
  filename: string,
  currentOrganizationId: any,
  currentPracticeId: any,
): Promise<{ uploadId: string; key: string }> => {
  const response = await axios.post(
    `${configurations.API_URL}/videa-notes/initiate-upload`,
    JSON.stringify({ filename }),
    {
      headers: getHeaders(currentOrganizationId, currentPracticeId),
    },
  );
  return response.data; // { uploadId, key }
};

/**
 * Retrieves presigned URLs to upload each part of a file during a multipart upload.
 *
 * @param {string} key - The S3 key of the file.
 * @param {string} uploadId - The upload ID for the multipart session.
 * @param {number} partsCount - The total number of parts the file will be split into.
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @returns {Promise<Array<{ signedUrl: string; partNumber: number }>>} A promise that resolves to an array of presigned URLs and part numbers.
 */
export const getPresignedUrls = async (
  key: string,
  uploadId: string,
  partsCount: number,
  currentOrganizationId: any,
  currentPracticeId: any,
): Promise<Array<{ signedUrl: string; partNumber: number }>> => {
  const response = await axios.post(
    `${configurations.API_URL}/videa-notes/presigned-urls`,
    JSON.stringify({ key, uploadId, partsCount }),
    {
      headers: getHeaders(currentOrganizationId, currentPracticeId),
    },
  );
  return response.data.urls; // array of { signedUrl, partNumber }
};

/**
 * Uploads individual file parts to presigned URLs during a multipart upload.
 *
 * @param {File} file - The file to upload.
 * @param {Array<{ signedUrl: string; partNumber: number }>} urls - An array of presigned URLs and corresponding part numbers.
 * @returns {Promise<Array<{ ETag: string | null; PartNumber: number }>>} A promise that resolves to an array of ETags and part numbers for completing the upload.
 * @throws Will throw an error if any part upload fails.
 */
export const uploadFileParts = async (
  file: File,
  urls: Array<{ signedUrl: string; partNumber: number }>,
): Promise<Array<{ ETag: string | null; PartNumber: number }>> => {
  const etags: { ETag: string | null; PartNumber: number }[] = [];

  for (const { partNumber, signedUrl } of urls) {
    const start = (partNumber - 1) * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blobPart = file.slice(start, end);

    const response = await fetch(signedUrl, {
      method: 'PUT',
      body: blobPart,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload part ${partNumber}`);
    }

    const etag = response.headers.get('ETag');
    etags.push({ ETag: etag, PartNumber: partNumber });
  }

  return etags;
};

/**
 * Completes a multipart upload by notifying the backend that all parts have been uploaded.
 *
 * @param {string} key - The S3 key of the file.
 * @param {string} uploadId - The multipart upload ID.
 * @param {Array<{ ETag: string | null; PartNumber: number }>} etags - The list of ETags and part numbers for each uploaded part.
 * @param {string | undefined} patientConsentRecordId - The patient consent record ID associated with the note.
 * @param {string | undefined} unifiedAppointmentId - The appointment ID associated with the note.
 * @param {string | undefined} standalonePatientVisitId - The standalone patient visit ID associated with the note.
 * @param {string | undefined} selectedPrompt - The selected prompt/template name.
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @returns {Promise<void>} A promise that resolves once the upload is completed.
 */
export const completeUpload = async (
  key: string,
  uploadId: string,
  etags: Array<{ ETag: string | null; PartNumber: number }>,
  patientConsentRecordId: string | undefined,
  unifiedAppointmentId: string | undefined,
  standalonePatientVisitId: string | undefined,
  selectedPrompt: string | undefined,
  currentOrganizationId: any,
  currentPracticeId: any,
): Promise<ClinicalNote> => {
  // Only send one of the IDs to avoid API error
  const requestBody: any = {
    key,
    uploadId,
    parts: etags,
    promptName: selectedPrompt,
    patientConsentRecordId,
  };

  // Add the appropriate ID based on which one is available
  if (unifiedAppointmentId) {
    requestBody.unifiedAppointmentId = unifiedAppointmentId;
  } else if (standalonePatientVisitId) {
    requestBody.standalonePatientVisitId = standalonePatientVisitId;
  }

  const response = await axios.post(
    `${configurations.API_URL}/videa-notes/complete-upload`,
    JSON.stringify(requestBody),
    {
      headers: getHeaders(currentOrganizationId, currentPracticeId),
    },
  );
  return response.data;
};

export enum PatientConsentStatus {
  YES = 'YES',
  NO = 'NO',
  WITHDRAWN = 'WITHDRAWN',
  NOT_REQUIRED = 'NOT_REQUIRED',
}

export type MarkConsentRequest = {
  consentStatus: PatientConsentStatus;
  isGivenByPatient: boolean;
  patientFullName: string;
  patientAppointmentAt: string;
  recordedBy: string;
  patientDob?: string;
};

export const createPatientConsentRecord = async ({
  consentRequest,
  unifiedAppointmentId,
  standalonePatientVisitId,
  currentOrganizationId,
  currentPracticeId,
}: {
  consentRequest: MarkConsentRequest;
  unifiedAppointmentId?: number;
  standalonePatientVisitId?: string;
  currentOrganizationId: any;
  currentPracticeId: any;
}) => {
  // Only send one of the IDs to avoid API error
  const requestBody: any = {
    consentStatus: consentRequest.consentStatus,
    isGivenByPatient: consentRequest.isGivenByPatient,
    patientFullName: consentRequest.patientFullName,
    patientAppointmentAt: consentRequest.patientAppointmentAt,
    recordedBy: consentRequest.recordedBy,
    patientDob: consentRequest.patientDob,
  };

  // Add the appropriate ID based on which one is available
  if (unifiedAppointmentId) {
    requestBody.unifiedAppointmentId = unifiedAppointmentId;
  } else if (standalonePatientVisitId) {
    requestBody.standalonePatientVisitId = standalonePatientVisitId;
  }

  const response = await axios.post(
    `${configurations.API_URL}/patient-consent-records`,
    JSON.stringify(requestBody),
    {
      headers: getHeaders(currentOrganizationId, currentPracticeId),
    },
  );
  return response.data;
};

/**
 * Constructs the HTTP headers required for API requests, including authorization and contextual identifiers.
 *
 * @param {any} currentOrganizationId - The current organization ID.
 * @param {any} currentPracticeId - The current practice ID.
 * @returns {{ [key: string]: string }} An object representing HTTP headers.
 */
const getHeaders = (
  currentOrganizationId: any,
  currentPracticeId: any,
): { [key: string]: string } => ({
  Authorization: `Bearer ${getAccessToken()}`,
  'Access-Control-Allow-Origin': '*',
  'Content-Type': 'application/json',
  practice: currentPracticeId,
  org: currentOrganizationId,
});
